/**
 * Razorpay integration for the Dyntra new UI.
 *
 * TEST MODE — use Razorpay's official DOMESTIC (Indian) test cards:
 *   Visa:        4386 2894 0766 0153
 *   Mastercard:  5267 3181 8797 5449
 *   Expiry:      any future date (e.g. 12/34)
 *   CVV:         any 3 digits (e.g. 123)
 *   OTP:         any value on the test 3-D Secure page (e.g. 1111)
 *
 * The backend is expected to hand us `{ key, orderId, amountMinor, currency }`
 * in the paymentInit payload. If that init is missing (no server keys set), we
 * fall back to a purely-local simulated success so the checkout flow still
 * demoes end-to-end.
 */

const RAZORPAY_SCRIPT_ID = "razorpay-checkout-js";
const RAZORPAY_SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";

function loadScript(src, id) {
  return new Promise((resolve, reject) => {
    if (typeof document === "undefined") {
      reject(new Error("document not available"));
      return;
    }
    if (id && document.getElementById(id)) {
      resolve();
      return;
    }
    const s = document.createElement("script");
    if (id) s.id = id;
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(s);
  });
}

export function loadRazorpayScript() {
  if (typeof window !== "undefined" && window.Razorpay) return Promise.resolve();
  return loadScript(RAZORPAY_SCRIPT_URL, RAZORPAY_SCRIPT_ID);
}

/**
 * Open Razorpay Checkout in test/live mode using the backend-provided init.
 */
export function openRazorpay({ key, orderId, amountMinor, currency = "INR", orderNumber, customer = {} }) {
  return new Promise((resolve, reject) => {
    if (!key || !orderId) {
      const err = new Error("Missing Razorpay key or order id");
      err.code = "MISSING_INIT";
      reject(err);
      return;
    }
    const options = {
      key,
      amount: amountMinor,
      currency,
      name: "Dyntra Store",
      description: orderNumber ? `Order ${orderNumber}` : "Store order",
      order_id: orderId,
      prefill: {
        name: customer.name || "",
        email: customer.email || "",
        contact: customer.phone || "",
      },
      theme: { color: "#4B2AAD" },
      handler(response) {
        resolve({
          providerOrderId: response.razorpay_order_id,
          providerPaymentId: response.razorpay_payment_id,
          signature: response.razorpay_signature,
        });
      },
      modal: {
        ondismiss() {
          const err = new Error("Payment cancelled");
          err.dismissed = true;
          reject(err);
        },
      },
    };
    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", (response) => {
      const err = new Error(response?.error?.description || "Payment failed");
      reject(err);
    });
    rzp.open();
  });
}

/**
 * High-level wrapper:
 *   - If backend returned a real Razorpay init → open the real modal.
 *   - Otherwise → simulate a successful test payment (for local demo builds).
 */
export async function runRazorpayPayment({ paymentInit, amountMinor, orderNumber, customer = {} }) {
  if (paymentInit && paymentInit.key && (paymentInit.orderId || paymentInit.order_id)) {
    await loadRazorpayScript();
    return openRazorpay({
      key: paymentInit.key,
      orderId: paymentInit.orderId || paymentInit.order_id,
      amountMinor: paymentInit.amountMinor || paymentInit.amount || amountMinor,
      currency: paymentInit.currency || "INR",
      orderNumber,
      customer,
    });
  }
  // Fallback: simulate a successful test payment.
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        providerOrderId: `mock_order_${Date.now()}`,
        providerPaymentId: `pay_mock_${Date.now()}`,
        signature: "mock_signature",
        mock: true,
      });
    }, 1200);
  });
}
